import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppConst from '../apis/appconst';
import logo from '../company logo.jpeg';
import { toast } from 'react-toastify';

const RenewalPage = () => {
  const navigate = useNavigate();
  const [licenseData, setLicenseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    checkLicenseStatus();
  }, []);

  const checkLicenseStatus = async () => {
    try {
      const res = await axios.get(`${AppConst.BASE_URL}${AppConst.CheckLicenseStatus}`);
      if (res.data && res.data.status) {
        setLicenseData(res.data.license);
      }
    } catch (err) {
      console.error('Error checking license:', err);
      toast.error('Failed to load license information');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayNow = async () => {
    setIsProcessing(true);

    try {
      // Get user data from session
      const stored = sessionStorage.getItem('ldata');
      let userId = null;
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          userId = parsed?.user_id;
        } catch (e) { /* ignore */ }
      }

      // Create Razorpay order
      const fd = new FormData();
      fd.append('user_id', userId || '');

      const orderRes = await axios.post(
        `${AppConst.BASE_URL}${AppConst.CreateRenewalOrder}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (!orderRes.data || !orderRes.data.status) {
        toast.error(orderRes.data?.message || 'Failed to create payment order');
        setIsProcessing(false);
        return;
      }

      const { order } = orderRes.data;

      // Initialize Razorpay
      const options = {
        key: order.key_id,
        amount: order.amount * 100, // Amount in paise
        currency: order.currency,
        name: 'Hospital Management System',
        description: 'Software License Renewal - 1 Year',
        image: logo,
        order_id: order.id,
        handler: async function (response) {
          // Payment successful - verify and process
          await verifyPayment(response, userId);
        },
        prefill: {
          name: '',
          email: '',
          contact: '9126538413'
        },
        notes: {
          license_renewal: 'true'
        },
        theme: {
          color: '#667eea'
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
            toast.info('Payment cancelled');
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      console.error('Payment error:', err);
      toast.error('Failed to initiate payment');
      setIsProcessing(false);
    }
  };

  const verifyPayment = async (paymentResponse, userId) => {
    try {
      const fd = new FormData();
      fd.append('razorpay_payment_id', paymentResponse.razorpay_payment_id);
      fd.append('razorpay_order_id', paymentResponse.razorpay_order_id);
      fd.append('razorpay_signature', paymentResponse.razorpay_signature);
      fd.append('user_id', userId || '');

      const res = await axios.post(
        `${AppConst.BASE_URL}${AppConst.VerifyAndRenew}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (res.data && res.data.status) {
        toast.success('🎉 License renewed successfully!', {
          autoClose: 2000
        });

        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
      } else {
        toast.error(res.data?.message || 'Payment verification failed');
        setIsProcessing(false);
      }

    } catch (err) {
      console.error('Verification error:', err);
      toast.error('Payment verification failed');
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="renewal-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="renewal-wrapper">
      {/* Animated Background */}
      <div className="animated-bg">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
      </div>

      <div className="renewal-container">
        <div className="renewal-card">
          {/* Header */}
          <div className="renewal-header">
            <img src={logo} alt="Company Logo" className="renewal-logo" />
            <h1 className="renewal-title">Software License Expired</h1>
          </div>

          {/* Expired Icon */}
          <div className="expired-icon-wrapper">
            <div className="expired-icon">
              <i className="ri-time-line"></i>
            </div>
          </div>

          {/* Message */}
          <div className="renewal-message">
            <h2>Kindly renew the software to continue</h2>
            {licenseData && (
              <div className="expiry-info">
                <p>
                  <i className="ri-calendar-line"></i>
                  Expired on: <strong>{new Date(licenseData.expiry_date).toLocaleDateString('en-IN')}</strong>
                </p>
                <p>
                  <i className="ri-information-line"></i>
                  License Key: <strong>{licenseData.license_key}</strong>
                </p>
              </div>
            )}
          </div>

          {/* Renewal Amount */}
          {licenseData && (
            <div className="renewal-amount">
              <div className="amount-label">Annual Renewal Fee</div>
              <div className="amount-value">
                ₹{licenseData.renewal_amount.toLocaleString('en-IN')}
                <span className="amount-period">/year</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="renewal-actions">
            <button
              className="btn-pay-now"
              onClick={handlePayNow}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Processing...
                </>
              ) : (
                <>
                  <i className="ri-bank-card-line me-2"></i>
                  Pay Now
                </>
              )}
            </button>

            <div className="contact-info">
              <p>Need assistance?</p>
              <a href="tel:+919126538413" className="contact-link">
                <i className="ri-phone-line"></i>
                +91 9126538413
              </a>
            </div>
          </div>

          {/* Features */}
          <div className="renewal-features">
            <h3>What you get with renewal:</h3>
            <ul>
              <li><i className="ri-check-line"></i> 1 Year Software Access</li>
              <li><i className="ri-check-line"></i> Free Updates & Bug Fixes</li>
              <li><i className="ri-check-line"></i> Priority Support</li>
              <li><i className="ri-check-line"></i> Data Backup & Security</li>
            </ul>
          </div>
        </div>
      </div>

      <style>{`
        .renewal-wrapper {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 2rem 1rem;
        }

        .animated-bg {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
          overflow: hidden;
          pointer-events: none;
        }

        .shape {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          animation: float 8s infinite ease-in-out;
        }

        .shape-1 {
          width: 400px;
          height: 400px;
          top: 10%;
          left: 10%;
        }

        .shape-2 {
          width: 300px;
          height: 300px;
          bottom: 20%;
          right: 15%;
          animation-delay: 2s;
        }

        .shape-3 {
          width: 250px;
          height: 250px;
          top: 50%;
          right: 10%;
          animation-delay: 4s;
        }

        @keyframes float {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.2; }
          50% { transform: translate(30px, -30px) rotate(180deg); opacity: 0.3; }
        }

        .renewal-loading {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .renewal-container {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 600px;
        }

        .renewal-card {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 3rem;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .renewal-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .renewal-logo {
          height: 60px;
          margin-bottom: 1rem;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .renewal-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: #dc2626;
          margin: 0;
        }

        .expired-icon-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 2rem;
        }

        .expired-icon {
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 3rem;
          color: white;
          box-shadow: 0 10px 30px rgba(239, 68, 68, 0.4);
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        .renewal-message {
          text-align: center;
          margin-bottom: 2rem;
        }

        .renewal-message h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 1rem;
        }

        .expiry-info {
          background: #fef3c7;
          border: 1px solid #fbbf24;
          border-radius: 12px;
          padding: 1rem;
          margin-top: 1rem;
        }

        .expiry-info p {
          margin: 0.5rem 0;
          font-size: 0.95rem;
          color: #92400e;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .expiry-info i {
          color: #f59e0b;
        }

        .renewal-amount {
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          border: 2px solid #3b82f6;
          border-radius: 16px;
          padding: 1.5rem;
          text-align: center;
          margin-bottom: 2rem;
        }

        .amount-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #1e40af;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.5rem;
        }

        .amount-value {
          font-size: 2.5rem;
          font-weight: 700;
          color: #1e3a8a;
        }

        .amount-period {
          font-size: 1rem;
          font-weight: 400;
          color: #3b82f6;
        }

        .renewal-actions {
          margin-bottom: 2rem;
        }

        .btn-pay-now {
          width: 100%;
          padding: 1.25rem;
          font-size: 1.125rem;
          font-weight: 600;
          color: white;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border: none;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 14px rgba(16, 185, 129, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .btn-pay-now:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.5);
        }

        .btn-pay-now:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .contact-info {
          text-align: center;
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e5e7eb;
        }

        .contact-info p {
          font-size: 0.875rem;
          color: #6b7280;
          margin-bottom: 0.5rem;
        }

        .contact-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1.125rem;
          font-weight: 600;
          color: #3b82f6;
          text-decoration: none;
          transition: color 0.2s;
        }

        .contact-link:hover {
          color: #2563eb;
        }

        .contact-link i {
          font-size: 1.25rem;
        }

        .renewal-features {
          background: #f9fafb;
          border-radius: 12px;
          padding: 1.5rem;
        }

        .renewal-features h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 1rem;
        }

        .renewal-features ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .renewal-features li {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 0;
          font-size: 0.95rem;
          color: #4b5563;
        }

        .renewal-features li i {
          color: #10b981;
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        @media (max-width: 768px) {
          .renewal-card {
            padding: 2rem 1.5rem;
          }

          .renewal-title {
            font-size: 1.5rem;
          }

          .renewal-message h2 {
            font-size: 1.25rem;
          }

          .amount-value {
            font-size: 2rem;
          }

          .expired-icon {
            width: 80px;
            height: 80px;
            font-size: 2.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default RenewalPage;